import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import InvestigationGuide from '../pages/InvestigationGuide';
import { getDeadlineStatus } from './DeadlineOverview';

const { FiFileText, FiClock, FiCheckCircle, FiAlertTriangle, FiChevronRight, FiSearch, FiBook, FiX, FiAlertCircle } = FiIcons;

const InvestigationList = () => {
  const navigate = useNavigate();
  const { investigations } = useInvestigation();
  const { reports = [] } = useData();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showGuidePreview, setShowGuidePreview] = useState(false);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase">Fullført</span>;
      case 'in_progress': return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold uppercase">Pågår</span>;
      case 'pending_start': return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-bold uppercase">Venter</span>;
      default: return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-[10px] font-bold uppercase">{status}</span>;
    }
  };

  const filtered = investigations.filter(inv => 
    inv.investigationTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.reportId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* HEADER WITH GUIDE ACCESS */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-black mb-2">Saksutredninger</h2>
            <p className="text-blue-100 max-w-xl">
              Her administrerer du alle formelle undersøkelser. Husk å følge prosedyrene beskrevet i veilederen for å sikre en juridisk korrekt prosess.
            </p>
          </div>
          <button 
            onClick={() => setShowGuidePreview(true)}
            className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-bold hover:bg-blue-50 transition-colors shadow-md flex items-center gap-2"
          >
            <SafeIcon icon={FiBook} /> Les Veileder Først
          </button>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mt-20 -mr-20 blur-3xl"></div>
      </div>

      {/* Guide Modal Preview - FIKSET LUKK-KNAPP FARGE */}
      <AnimatePresence>
        {showGuidePreview && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowGuidePreview(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-900 w-full max-w-4xl h-[90vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl" 
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                <h3 className="font-bold text-gray-900 dark:text-white">Veileder for saksbehandling</h3>
                <button 
                  onClick={() => setShowGuidePreview(false)} 
                  className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-all"
                >
                  <SafeIcon icon={FiX} size={24} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <InvestigationGuide isEmbedded={true} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SEARCH AND LIST */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Aktive saker</h3>
          <div className="relative w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk i utredninger..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          {filtered.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Utredning</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Friststatus</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Ansvarlig</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Startet</th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Handling</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filtered.map((inv) => {
                    const report = reports.find(r => r.id === inv.reportId);
                    const dlStatus = report ? getDeadlineStatus(report) : 'on_track';
                    const isUrgent = dlStatus.startsWith('overdue') || dlStatus.startsWith('urgent');

                    return (
                      <tr 
                        key={inv.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group cursor-pointer"
                        onClick={() => navigate(`/company/investigation/form/${inv.reportId}`)}
                      >
                        <td className="px-6 py-4">
                          <p className="font-bold text-gray-900 dark:text-white text-sm">{inv.investigationTitle}</p>
                          <p className="text-[10px] text-gray-400 font-mono">ID: {inv.id}</p>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(inv.status)}
                        </td>
                        <td className="px-6 py-4">
                          {isUrgent ? (
                            <span className="flex items-center gap-1 text-[10px] font-black bg-red-100 text-red-700 px-2 py-0.5 rounded-full uppercase">
                              <SafeIcon icon={FiAlertCircle} size={10} /> {dlStatus.includes('ack') ? 'Mottak' : 'Frist'}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-gray-400 uppercase">I rute</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {inv.investigator}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-500">
                          {inv.startDate || inv.createdAt}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button className="text-indigo-600 dark:text-indigo-400 font-bold text-xs uppercase tracking-widest hover:underline flex items-center justify-end gap-1 ml-auto">
                            Åpne Skjema <SafeIcon icon={FiChevronRight} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-20 text-center">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiFileText} className="text-3xl text-gray-300 dark:text-gray-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Ingen utredninger funnet</h3>
              <p className="text-gray-500 text-sm mt-1">Start en utredning direkte fra en varslingssak.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestigationList;